import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AppModal from './web/AppModal';
import { useTheme } from '../context/ThemeContext';
import { modalFooterButtonStyles } from '../theme/modalButtons';

export type AppMessageVariant = 'success' | 'error' | 'info';

type Props = {
  visible: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  variant?: AppMessageVariant;
  onConfirm: () => void;
};

const iconForVariant = (variant: AppMessageVariant): React.ComponentProps<typeof Ionicons>['name'] => {
  switch (variant) {
    case 'success':
      return 'checkmark-circle';
    case 'error':
      return 'alert-circle';
    default:
      return 'information-circle';
  }
};

/** Styled alert dialog consistent with AppModal / in-app modals. */
const AppMessageModal = ({
  visible,
  title,
  message,
  confirmLabel = 'OK',
  variant = 'info',
  onConfirm,
}: Props) => {
  const { colors } = useTheme();

  const accentColor =
    variant === 'success'
      ? colors.success.main
      : variant === 'error'
        ? colors.error.main
        : colors.brand.accent;

  return (
    <AppModal
      visible={visible}
      onRequestClose={onConfirm}
      size="sm"
      scrollable={false}
      showCloseButton={false}
      animationType="fade"
    >
      <View style={styles.content}>
        <View style={[styles.iconWrap, { backgroundColor: `${accentColor}22` }]}>
          <Ionicons name={iconForVariant(variant)} size={40} color={accentColor} />
        </View>
        <Text style={[styles.title, { color: colors.text.primary }]}>{title}</Text>
        <Text style={[styles.message, { color: colors.text.secondary }]}>{message}</Text>
        <TouchableOpacity
          style={[modalFooterButtonStyles.primary, styles.button]}
          onPress={onConfirm}
          accessibilityRole="button"
        >
          <Text style={modalFooterButtonStyles.primaryText}>{confirmLabel}</Text>
        </TouchableOpacity>
      </View>
    </AppModal>
  );
};

const styles = StyleSheet.create({
  content: {
    alignItems: 'center',
    paddingTop: 4,
  },
  iconWrap: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
  },
  message: {
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: 24,
  },
  button: {
    alignSelf: 'stretch',
    minHeight: 48,
  },
});

export default AppMessageModal;
